import { Component, OnInit, DestroyRef, inject, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule, NgClass } from '@angular/common';
import { ProfileComponent } from '../profile/profile.component';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { AuthorizationService } from '../../services/authorization.service';
import { Permissions } from '../../shared/permissions';
import { RoutePaths } from '../../shared/types/common';
import { StateService } from '../../services/state.service';
import { ComingSoonComponent } from '../../shared/components/coming-soon/coming-soon.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetVersionPipe } from '../../pipes/asset-version.pipe';
import { tooltips } from '../../utils/tooltips';
import { PremiumTooltipComponent } from '../../shared/components/premium-tooltip/premium-tooltip.component';
import { TippyDirective } from '@ngneat/helipopper';
import { PosthogService } from '../../services/posthog.service';
import { PosthogEventName, PosthogEventCode } from '../../services/types';

interface MenuItem {
  route: RoutePaths | null;
  text: string;
  icon?: string;
  disable: boolean;
  comingSoon: boolean;
  premiumBadge?: boolean;
}

interface MenuGroup {
  id: string;
  text: string;
  icon: string;
  collapsible: boolean;
  expanded: boolean;
  comingSoon: boolean;
  premiumBadge: boolean;
  items: MenuItem[];
}

interface MenuTopItem {
  route: RoutePaths;
  icon: string;
  text: string;
  disable: boolean;
  comingSoon: boolean;
  premiumBadge: boolean;
  badge?: string;
}

interface MenuCategory {
  label: string;
  groups?: MenuGroup[];
  items?: MenuItem[];
}

@Component({
  selector: 'app-main-side-menu',
  imports: [
    NgClass,
    ProfileComponent,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ComingSoonComponent,
    AssetVersionPipe,
    CommonModule,
    TippyDirective,
    PremiumTooltipComponent
  ],
  templateUrl: './main-side-menu.component.html',
  styleUrl: './main-side-menu.component.scss'
})
export class MainSideMenuComponent implements OnInit {

  public isProfileOpen: boolean = false;
  public disabledTabs: Partial<Record<RoutePaths, boolean>> = {};
  public profilePositions = [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: 0 }] as ConnectedPosition[];
  public RoutePaths = RoutePaths;
  protected readonly tooltips = tooltips;
  protected readonly dataCatalogUrl = 'https://browsi.atlassian.net/wiki/spaces/IH/pages/3525869580/Data+Catalog?utm_source=app&utm_medium=side_menu';

  public isCopilotHovered: boolean = false;
  public hoveredMap: Record<string, boolean> = {};

  private posthogService = inject(PosthogService);
  private destroy$ = inject(DestroyRef);
  public userGivenName: string = '';
  public userFamilyName: string = '';

  public topItems: MenuTopItem[] = [];
  public categories: MenuCategory[] = [];
  public showScrollGradient: boolean = false;

  @ViewChild('menuItemsRef') menuItemsRef!: ElementRef<HTMLDivElement>;

  constructor(private router: Router, private authorizationService: AuthorizationService, private stateService: StateService) { }

  ngOnInit() {
    this.stateService.loading = true;
    this.authorizationService.userProfile$.pipe(
      takeUntilDestroyed(this.destroy$)
    ).subscribe(user => {
      this.userGivenName = user.firstName;
      this.userFamilyName = user.lastName;
    });
    this.authorizationService.permissions$.pipe(
      takeUntilDestroyed(this.destroy$)
    ).subscribe(permissions => {
      if (permissions.length > 0) {
        this.setDisabledTabs();
        this.buildTopItems();
        this.buildCategories();
        this.stateService.loading = false;
        setTimeout(() => this.checkScrollGradient());
      }
    });
  }

  onMenuScroll() {
    this.checkScrollGradient();
  }

  private checkScrollGradient() {
    if (!this.menuItemsRef) return;
    const el = this.menuItemsRef.nativeElement;
    const isScrollable = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 2;
    this.showScrollGradient = isScrollable && !isAtBottom;
  }

  get currentRoute(): string {
    return this.router.url.split('?')[0];
  }

  isActiveRoute(route: RoutePaths | null): boolean {
    if (!route) return false;
    const parts = this.currentRoute.slice(1).split('/');

    const isAlertCenterRoute = this.currentRoute.startsWith('/' + RoutePaths.ALERT_CENTER) || this.currentRoute.startsWith('/' + RoutePaths.ALERT_VIEW);
    if (isAlertCenterRoute) {
      return route === RoutePaths.ALERT_CENTER;
    }

    return parts[0] === route;
  }

  private isDisabledRoute(route: RoutePaths): boolean {
    return this.disabledTabs[route] ?? false;
  }

  private getRouteExtraConfig(route: RoutePaths, isSocialSubItem: boolean = false): { disable: boolean; comingSoon: boolean; premiumBadge: boolean; } {
    const isDisabledTab = this.disabledTabs[route] ?? false;
    const premiumBadge = (this.isTrialUser || this.isExpiredUser) && [RoutePaths.AI_PRESENCE, RoutePaths.AUDIENCE, RoutePaths.REPUTATION].includes(route);
    const comingSoon = this.isExpiredUser || isSocialSubItem ? false :
      this.isTrialUser ? isDisabledTab && !premiumBadge:
      isDisabledTab;
    return {
      disable: isDisabledTab,
      comingSoon,
      premiumBadge,
    }
  }

  navigate(route: string): void {
    this.isProfileOpen = false;
    this.router.navigate([`/${route}`])
  }

  navigateIfEnabled(route: RoutePaths | null): void {
    if (!route) return;
    if (route === RoutePaths.DATA_POINTS) {
      this.openDataCatalog();
      return;
    }
    if (!this.disabledTabs[route]) {
      this.navigate(route);
    }
  }

  toggleGroup(group: MenuGroup): void {
    if (group.collapsible) {
      group.expanded = !group.expanded;
      setTimeout(() => this.checkScrollGradient());
    }
  }

  openProfileScreen() {
    this.isProfileOpen = !this.isProfileOpen
  }

  openDataCatalog() {
    this.posthogService.capture(PosthogEventName.EXTERNAL_LINK_CLICKED, {
      [PosthogEventCode.CLICK_URL]: this.dataCatalogUrl,
      [PosthogEventCode.CLICK_TEXT]: 'Data Catalog'
    });
    window.open(this.dataCatalogUrl, '_blank');
  }

  setDisabledTabs() {
    this.disabledTabs[RoutePaths.MY_BRAND] = !this.authorizationService.hasPermissions([Permissions.READ_MY_BRAND_DASHBOARD]);
    this.disabledTabs[RoutePaths.COMPETITION] = !this.authorizationService.hasPermissions([Permissions.READ_MY_COMPETITION_DASHBOARD]);
    this.disabledTabs[RoutePaths.MESSAGING] = !this.authorizationService.hasPermissions([Permissions.READ_MESSAGING_SCREEN]);
    this.disabledTabs[RoutePaths.CAMPAIGNS] = !this.authorizationService.hasPermissions([Permissions.READ_MY_CAMPAIGNS_DASHBOARD]);
    this.disabledTabs[RoutePaths.POLARIS] = !this.authorizationService.hasPermissions([Permissions.READ_COPILOT_SCREEN]);
    this.disabledTabs[RoutePaths.AI_PRESENCE] = !this.authorizationService.hasPermissions([Permissions.READ_AI_PRESENCE_SCREEN]);
    this.disabledTabs[RoutePaths.ALERT_CENTER] = !this.authorizationService.hasPermissions([Permissions.READ_POLARIS_ALERTS, Permissions.WRITE_POLARIS_ALERTS]);
    this.disabledTabs[RoutePaths.DATA_POINTS] = !this.authorizationService.hasPermissions([Permissions.READ_DATAPOINT_DOC]);
    this.disabledTabs[RoutePaths.AUDIENCE] = true;
    this.disabledTabs[RoutePaths.SOCIAL_OVERVIEW] = !this.authorizationService.hasPermissions([Permissions.READ_SOCIAL_OVERVIEW_SCREEN]);
    this.disabledTabs[RoutePaths.SOCIAL] = !this.authorizationService.hasPermissions([Permissions.READ_SOCIAL_OVERVIEW_SCREEN]);
  }

  private buildTopItems() {
    this.topItems = [
      {
        route: RoutePaths.POLARIS,
        icon: 'svg/copilot-star.svg',
        text: 'POLARIS™',
        badge: 'Beta',
        ...this.getRouteExtraConfig(RoutePaths.POLARIS)
      },
    ];

    if (this.shouldDisplaySignals) {
      this.topItems.push({
        route: RoutePaths.SIGNALS,
        icon: 'svg/signals-logo.svg',
        text: 'Signals',
        ...this.getRouteExtraConfig(RoutePaths.SIGNALS)
      });
    }

    this.topItems.push({
      route: RoutePaths.ALERT_CENTER,
      icon: this.isDisabledRoute(RoutePaths.ALERT_CENTER) ? 'svg/alerts-tab-disabled.svg' : 'svg/alerts-tab.svg',
      text: 'Alert Center',
      ...this.getRouteExtraConfig(RoutePaths.ALERT_CENTER)
    });
  }

  private buildCategories() {
    this.categories = [
      {
        label: 'Advertising',
        groups: [
          {
            id: 'open-web',
            text: 'Open Web',
            icon: 'svg/globe.svg',
            collapsible: true,
            expanded: true,
            comingSoon: false,
            premiumBadge: false,
            items: [
              {
                route: RoutePaths.MY_BRAND,
                text: 'My Brand',
                disable: this.isDisabledRoute(RoutePaths.MY_BRAND),
                comingSoon: false,
              },
              {
                route: RoutePaths.COMPETITION,
                text: 'My Competition',
                disable: this.isDisabledRoute(RoutePaths.COMPETITION),
                comingSoon: false,
              },
              {
                route: RoutePaths.MESSAGING,
                text: 'Creatives',
                disable: this.isDisabledRoute(RoutePaths.MESSAGING),
                comingSoon: false,
              },
              {
                route: RoutePaths.AUDIENCE,
                text: 'Audiences',
                disable: true,
                comingSoon: true,
              },
            ]
          },
          {
            id: 'social',
            text: 'Social',
            icon: 'svg/Social.svg',
            collapsible: true,
            expanded: true,
            comingSoon: false,
            premiumBadge: this.isDisabledRoute(RoutePaths.SOCIAL_OVERVIEW),
            items: [
              { route: this.isTrialUser ? RoutePaths.SOCIAL_OVERVIEW : RoutePaths.SOCIAL,
                text: 'Overview',
                ...this.getRouteExtraConfig(RoutePaths.SOCIAL_OVERVIEW, true) },
              { route: null, text: 'Creatives', disable: true, comingSoon: false},
            ]
          },
        ]
      },
      {
        label: 'Organic',
        items: [
          {
            route: RoutePaths.AI_PRESENCE,
            icon: this.isDisabledRoute(RoutePaths.AI_PRESENCE) ? 'svg/ai-presence-gray.svg' : 'svg/ai-presence.svg',
            text: 'AI Presence',
            ...this.getRouteExtraConfig(RoutePaths.AI_PRESENCE)
          }
        ]
      },
      {
        label: 'My Data',
        items: [
          {
            route: RoutePaths.CAMPAIGNS,
            icon: this.isDisabledRoute(RoutePaths.CAMPAIGNS) ? 'svg/my-campaigns-tab-disabled.svg' : 'svg/my-campaigns-tab.svg',
            text: 'My Campaigns',
            ...this.getRouteExtraConfig(RoutePaths.CAMPAIGNS)
          }
        ]
      },
      {
        label: 'More',
        items: [
          {
            route: RoutePaths.DATA_POINTS,
            icon: this.isDisabledRoute(RoutePaths.DATA_POINTS) ? 'svg/data-catalog-disabled.svg' : 'svg/data-catalog.svg',
            text: 'Data Catalog',
            ...this.getRouteExtraConfig(RoutePaths.DATA_POINTS)
          }
        ]
      }
    ];
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.isProfileOpen = false;
    }
  }

  get isTrialUser(): boolean {
    return this.authorizationService.isTrialUser;
  }

  get isExpiredUser(): boolean {
    return this.authorizationService.isExpiredUser;
  }

  get shouldDisplaySignals(): boolean {
    return this.authorizationService.hasPermissions([Permissions.READ_SIGNAL]);
  }
}